/**
 * The second floating layer in the app, and the first one that covers the page.
 *
 * IT REVERSES A RULE THAT WAS WRITTEN DOWN, so here is why. `Settings.tsx` used to say
 * "there is no modal anywhere in this app", and for the inline forms it was written about
 * that is still the right call: "Add somebody" is one email box, and a dialog for it would
 * be ceremony around a single field. A category is the opposite shape. It has a name, a
 * definition that is read on every call, a warning about the other list, and a delete that
 * has to ask where some number of already-sorted calls should go - and all of that used to
 * be crammed into one 168px box beside one 1fr box, with the delete question unfolding
 * inside the row and pushing the rest of the list down the page.
 *
 * IT DOES NOT FLOAT EITHER, and that is the part of the old rule that survives. There are
 * no shadow tokens in this product (see `styles.css`) and nothing is lifted off the page.
 * What separates a dialog from what it covers is the SCRIM - which is the same idea as a
 * border, applied to the whole background at once, rather than a soft edge on one box.
 *
 * IT COMES IN THREE SIZES NOW. `form` is what the paragraphs above were written about. The
 * widest is the call: a panel split into a transcript and a reading, opened over the list of
 * calls rather than navigated to. `list` sits between them, for a column of rows somebody
 * picks one of - the voice picker. Nothing in the reasoning above changes for any of them -
 * same scrim, same escape hatches, same absence of a shadow - but the two bounded ones put
 * the overflow on the BODY rather than on the panel, so the footer stays on screen however
 * many rows there are. `bare` additionally hands the padding and the overflow to the caller,
 * which is what the call needs and a list does not.
 *
 * WHAT IT OWNS: the escape hatches, through `useModalLayer`, which the tool editor and the
 * Try it drawer share. Escape, the scrim, and the close button all mean the
 * same thing; focus moves in on open and back to whatever opened it on close; Tab cycles
 * inside rather than wandering into the page underneath; and the page stops scrolling
 * behind it. None of that is optional for a layer that covers the screen, and every one of
 * them is the kind of thing a second hand-rolled dialog would get slightly wrong - which is
 * the argument for this file existing at all.
 */

import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconButton, cx } from "../ui";
import { Close } from "./Icons";
import { useModalLayer } from "./useModalLayer";

/**
 * The two shapes a dialog comes in.
 *
 * `form` is what this file was written for and is unchanged: a column of fields, as wide
 * as a column of fields should be, scrolling as one. `wide` is the call - a bounded panel
 * whose CHILDREN scroll, which is why it is `flex flex-col` with `overflow-hidden` here
 * and `min-h-0` on everything below. `max-h` rather than a fixed height on purpose: a
 * four-turn call gets a short dialog instead of 92vh of white.
 *
 * 1040, DOWN FROM 1440 (2026-09-28). At 1440 the panes pulled apart - dates a hand's width
 * from their lines, labels a screen from their values - and the dialog read as a page. The
 * reading column is 40% of it, about 416px, so `QualificationDetail` (container query at
 * 560px) sits on its stacked layout: the score ring above its five measures rather than
 * beside them. That is the layout it was built to fall back to, not a break.
 *
 * Under 720px a wide dialog stops being a dialog and becomes the screen. A 96vw panel
 * with a 14px radius on a phone is a card pretending there is something behind it.
 */
const PANEL: Record<"form" | "list" | "wide", string> = {
  form: "w-[min(92vw,520px)] max-h-[85vh] overflow-y-auto",
  /*
   * THE THIRD SIZE, AND IT IS A WIDTH CHOSEN FROM ONE LINE OF TEXT. A list dialog holds rows
   * that are a name, a line describing it and a control on the right - the voice picker's
   * "Andromeda / American · Feminine · Casual, expressive, comfortable / Play". The longest
   * of those descriptions is about 400px at 12px, so a content box around 700px puts the
   * control a readable distance from the words. At `wide` the same row stretched to 1400px
   * and the play button ended up a hand's width from the name it belonged to, with the whole
   * middle of every row empty.
   *
   * It scrolls the way `form` does - as one column, not as panes - which is what a list
   * wants; `wide` splits into two independently scrolling panes and that is the difference
   * between the two.
   */
  list:
    "flex w-[min(94vw,760px)] max-h-[85vh] flex-col overflow-hidden " +
    "max-[720px]:h-[100dvh] max-[720px]:max-h-none max-[720px]:w-full " +
    "max-[720px]:rounded-none max-[720px]:border-0",
  // 1040px - see the header for why it came down from 1440.
  wide:
    "flex w-[min(96vw,1040px)] max-h-[92vh] flex-col overflow-hidden " +
    "max-[720px]:h-[100dvh] max-[720px]:max-h-none max-[720px]:w-full " +
    "max-[720px]:rounded-none max-[720px]:border-0",
};

export function Dialog({
  title,
  onClose,
  footer,
  children,
  labelledBy,
  size = "form",
  bare,
  lead,
  meta,
  aside,
}: {
  title: ReactNode;
  onClose: () => void;
  /** The row of buttons along the bottom. Laid out by the caller; separated by this file. */
  footer?: ReactNode;
  children: ReactNode;
  /** Overrides the generated id, for a caller that wants to name the dialog itself. */
  labelledBy?: string;
  size?: "form" | "list" | "wide";
  /** The body owns its own padding and its own scrolling. For a panelled layout. */
  bare?: boolean;
  /*
   * `meta` and `aside` are the two slots `PageHead` already has, and they are here for the
   * same reason they are there: a header that a caller replaces wholesale is a header a
   * caller can ship without a close button, which is the exact class of mistake the list
   * below exists to prevent.
   */
  /**
   * Small facts BESIDE the title, on its own line.
   *
   * IT IS OUTSIDE THE `h2` AND THAT IS THE ENTIRE POINT OF IT EXISTING. The heading is what
   * `aria-labelledby` points at, so a caller that puts a badge and a meter into `title`
   * makes the dialog's accessible name "9702 Caller 9702 Booked 62 of 100" and takes the
   * `truncate` off the one part of it that is a name. This slot is the place for the things
   * that qualify the title without being it.
   */
  lead?: ReactNode;
  /** Small facts under the title - a badge, a number, a date. */
  meta?: ReactNode;
  /** Controls between the title and the close button. */
  aside?: ReactNode;
}) {
  /*
   * A BOUNDED PANEL WHOSE BODY SCROLLS, which is both of the sizes that are not `form`. It
   * decides three things together, and they have to stay together: the rule under the
   * header, the fact that the body rather than the panel is what overflows, and the footer
   * being pinned. A `form` dialog is the opposite of all three - it scrolls as one piece and
   * its title sits directly on its own fields.
   */
  const banded = size !== "form";
  const wide = size === "wide";
  const panel = useRef<HTMLDivElement>(null);
  const generated = useId();
  const heading = labelledBy ?? `${generated}-title`;

  // Escape, focus in and back out, the Tab trap and the scroll lock. See `useModalLayer`.
  useModalLayer(panel, onClose);

  return createPortal(
    <div
      /*
       * The scrim is this product's substitute for a drop shadow. `color-mix` against the
       * ink token rather than a bare black, so it is the same neutral everything else on
       * the page is drawn from and does not turn the white card blue-grey at the edges.
       */
      className={cx(
        "fixed inset-0 z-50 grid place-items-center overflow-y-auto p-4 max-[720px]:p-0",
        "bg-[color-mix(in_srgb,var(--color-ink)_38%,transparent)]",
      )}
      // `mousedown` and not `click`: a press that STARTS inside the panel and ends outside
      // it - releasing a text selection past the edge - is not a request to close.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={heading}
        tabIndex={-1}
        className={cx("outline-none rounded-lg border border-line bg-paper", PANEL[size])}
      >
        {/* A rule under a wide dialog's header and none under a form's: the wide one is a
            bounded band over two scrolling panes and has to stay legible as content slides
            under it, and the form's title sits directly on its own fields. */}
        <div
          className={cx(
            "flex items-start justify-between gap-3 px-5",
            banded ? "flex-none flex-wrap border-b border-line py-3.5" : "pt-4.5 pb-1",
          )}
        >
          <div className="grid min-w-0 gap-1.5">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
              <h2
                id={heading}
                className={cx("font-semibold text-ink", wide ? "truncate text-lg" : "text-base")}
              >
                {title}
              </h2>
              {lead}
            </div>
            {meta && (
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-ink">
                {meta}
              </div>
            )}
          </div>
          <div className="flex flex-none items-center gap-1">
            {aside}
            <IconButton type="button" className="-mr-1" onClick={onClose}>
              <Close size={16} />
              <span className="sr-only">Close</span>
            </IconButton>
          </div>
        </div>

        {/*
          `min-h-0` is what lets a flex child SCROLL rather than grow past its parent. It has
          to be unbroken from here down to whichever element carries the overflow.

          THE NON-`bare` BODY NEEDS IT TOO IN A BOUNDED PANEL, and it did not have it. A flex
          item's `min-height` is `auto`, so a body full of rows refuses to shrink, grows past
          the panel, is clipped by its `overflow-hidden` - and takes the FOOTER with it. The
          symptom is a dialog whose Save button simply is not on the screen, which is exactly
          what the voice picker shipped with until somebody opened it with forty voices in it.
        */}
        <div
          className={cx(
            bare ? "flex" : "grid gap-4 px-5 py-3.5",
            bare || banded ? "min-h-0 flex-1" : "",
            banded && !bare && "overflow-y-auto",
          )}
        >
          {children}
        </div>

        {footer && (
          // A rule above the buttons, because they are the one part of a dialog that is
          // not about the thing being edited - and the whole product separates with lines.
          <div className="flex flex-none flex-wrap items-center gap-2 border-t border-line-soft px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
